import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { companiesApi, type CnpjConflictPayload } from '@/features/companies/companies.api';
import { isValidCNPJ } from '@/utils/validators';
import { formatCNPJ, stripCNPJ } from '@/utils/cnpj';
import { gbToBytes } from '@/utils/bytes';
import { getErrorMessage } from '@/utils/errorHandling';
import { useNotification } from '@/contexts/NotificationContext';

const schema = z.object({
  cnpj: z
    .string()
    .min(1, 'CNPJ é obrigatório')
    .refine(v => isValidCNPJ(v), 'CNPJ inválido'),
  legalName: z.string().min(1, 'Razão social é obrigatória'),
  customerLimit: z.coerce.number().int().positive().optional(),
  aiTokenLimit: z.coerce.number().int().positive().optional(),
  storageLimitGb: z.coerce.number().positive().optional(),
});

type FormData = z.infer<typeof schema>;

interface ReactivationSuggestion {
  companyId: string;
  legalName: string;
  deletedAt: string;
}

export const CompanyCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const [error, setError] = useState<string | null>(null);
  const [reactivation, setReactivation] = useState<ReactivationSuggestion | null>(null);
  const [reactivating, setReactivating] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      cnpj: '',
      legalName: '',
      customerLimit: undefined,
      aiTokenLimit: undefined,
      storageLimitGb: undefined,
    },
  });

  const createMutation = useMutation({
    mutationFn: companiesApi.create,
    onSuccess: company => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      showNotification('Empresa criada com sucesso', 'success');
      navigate(`/companies/${company.id}`);
    },
    onError: (err: unknown) => {
      setReactivation(null);
      if (err instanceof AxiosError && err.response?.status === 409) {
        const payload = err.response.data as CnpjConflictPayload;
        if (payload.companyId && payload.deletedAt && payload.legalName) {
          setReactivation({
            companyId: payload.companyId,
            legalName: payload.legalName,
            deletedAt: payload.deletedAt,
          });
          setError(null);
          return;
        }
      }
      setError(getErrorMessage(err, 'Erro ao criar empresa'));
    },
  });

  const onSubmit = (data: FormData) => {
    setError(null);
    setReactivation(null);
    createMutation.mutate({
      cnpj: stripCNPJ(data.cnpj),
      legalName: data.legalName,
      customerLimit: data.customerLimit,
      aiTokenLimit: data.aiTokenLimit,
      storageLimit: data.storageLimitGb !== undefined ? gbToBytes(data.storageLimitGb) : undefined,
    });
  };

  const handleReactivate = async () => {
    if (!reactivation) return;
    setReactivating(true);
    try {
      const company = await companiesApi.activate(reactivation.companyId);
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      showNotification('Empresa reativada com sucesso', 'success');
      navigate(`/companies/${company.id}`);
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao reativar empresa'));
    } finally {
      setReactivating(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 1 }}>
        Nova empresa
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Cadastre um novo tenant. Os limites são opcionais (default vem da configuração do sistema).
      </Typography>

      <Paper sx={{ p: 4, maxWidth: 720 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {reactivation && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleReactivate}
                disabled={reactivating}
              >
                {reactivating ? 'Reativando…' : 'Reativar'}
              </Button>
            }
          >
            Já existe uma empresa desativada com este CNPJ:{' '}
            <strong>{reactivation.legalName}</strong> (desativada em{' '}
            {new Date(reactivation.deletedAt).toLocaleDateString('pt-BR')}).
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2}>
            <Controller
              name="cnpj"
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <TextField
                  {...rest}
                  value={formatCNPJ(value ?? '')}
                  onChange={e => onChange(formatCNPJ(e.target.value))}
                  fullWidth
                  label="CNPJ"
                  placeholder="AA.AAA.AAA/AAAA-00"
                  error={!!errors.cnpj}
                  helperText={errors.cnpj?.message}
                  disabled={createMutation.isPending}
                  slotProps={{ htmlInput: { autoCapitalize: 'characters', maxLength: 18 } }}
                />
              )}
            />

            <Controller
              name="legalName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Razão social"
                  error={!!errors.legalName}
                  helperText={errors.legalName?.message}
                  disabled={createMutation.isPending}
                />
              )}
            />

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Limites custom (opcional)
            </Typography>

            <Controller
              name="customerLimit"
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <TextField
                  {...rest}
                  value={value ?? ''}
                  onChange={e => onChange(e.target.value === '' ? undefined : e.target.value)}
                  fullWidth
                  type="number"
                  label="Limite de clientes"
                  error={!!errors.customerLimit}
                  helperText={errors.customerLimit?.message}
                  disabled={createMutation.isPending}
                />
              )}
            />

            <Controller
              name="aiTokenLimit"
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <TextField
                  {...rest}
                  value={value ?? ''}
                  onChange={e => onChange(e.target.value === '' ? undefined : e.target.value)}
                  fullWidth
                  type="number"
                  label="Limite de tokens de IA"
                  error={!!errors.aiTokenLimit}
                  helperText={errors.aiTokenLimit?.message}
                  disabled={createMutation.isPending}
                />
              )}
            />

            <Controller
              name="storageLimitGb"
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <TextField
                  {...rest}
                  value={value ?? ''}
                  onChange={e => onChange(e.target.value === '' ? undefined : e.target.value)}
                  fullWidth
                  type="number"
                  label="Limite de storage (GB)"
                  error={!!errors.storageLimitGb}
                  helperText={errors.storageLimitGb?.message}
                  disabled={createMutation.isPending}
                  slotProps={{ htmlInput: { step: '0.1', min: '0' } }}
                />
              )}
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
              <Button
                component={RouterLink}
                to="/companies"
                variant="outlined"
                disabled={createMutation.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Criar empresa'
                )}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};
